import React, { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';

import { InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { Popover } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import styled from 'styled-components';

import { deleteRingCentralCommunication } from 'apis/RingCentralAPI';
import DownCaretIcon from 'common/Icons/DownCaretIcon';
import ConfirmModal from 'common/Modal/ConfirmModal';
import { useRootContext } from 'components/layout/RootContext';
import Dropdown from 'components/lib/Dropdown';
import { SimpleListActionButtonContent, SimpleListActionsButton } from 'components/lib/SimpleListModalComponents';

import RingCentralRecordMediaModal from './RingCentralRecordMediaModal';
import { useRingCentralRecordsContext } from './RingCentralRecordsContext';
import {
  Badge,
  BadgeGroup,
  ContactValue,
  formatDate,
  formatDuration,
  PrimaryValue,
  RingCentralRecordsStyledTable,
  RingCentralRecordTag,
  SecondaryValue,
  ValueContainer,
} from './RingCentralRecordsDisplay';
import { RingCentralCommunicationType } from './types';

const Container = styled.div`
  position: relative;
  display: flex;
  min-height: 0;
`;

const ActionsCell = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const RawStatusHeader = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const RawStatusInfoButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--text-secondary);
  cursor: help;
  line-height: 1;
`;

const renderRecordsTotal = (total: number, range: number[]) => {
  const displayedItems = range[1] - range[0] + 1;
  return (
    <span>
      Showing {displayedItems} of {total} records
    </span>
  );
};

const getRawStatus = (record: RingCentralCommunicationType) => {
  if (record.source_type === 'call') {
    return record.result || record.provider_status || '—';
  }

  return record.provider_status || record.result || '—';
};

const RingCentralRecordsTable = () => {
  const { showMessage } = useRootContext();
  const queryClient = useQueryClient();
  const { loading, items, totalItemsCount, filters, setPage } = useRingCentralRecordsContext();
  const [selectedRecordId, setSelectedRecordId] = useState<number | null>(null);
  const [recordToDelete, setRecordToDelete] = useState<RingCentralCommunicationType | null>(null);

  const { mutate: deleteRecord, isLoading: isDeleting } = useMutation(
    () => deleteRingCentralCommunication(recordToDelete?.id as number),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['ringcentral-communications']);
        showMessage('success', 'RingCentral record deleted.');
        setRecordToDelete(null);
      },
      onError: () => showMessage('error', 'Unable to delete RingCentral record.'),
    }
  );

  const columns = useMemo<ColumnsType<RingCentralCommunicationType>>(
    () => [
      {
        title: 'Communication',
        key: 'record',
        width: '18%',
        render: (_value: unknown, record: RingCentralCommunicationType) => (
          <ValueContainer>
            <PrimaryValue>{record.external_id}</PrimaryValue>
            <BadgeGroup>
              <RingCentralRecordTag value={record.source_type} />
              <RingCentralRecordTag value={record.outcome || '—'} />
              <RingCentralRecordTag value={record.direction} />
            </BadgeGroup>
          </ValueContainer>
        ),
      },
      {
        title: 'From',
        key: 'from',
        width: '14%',
        render: (_value: unknown, record: RingCentralCommunicationType) => (
          <ContactValue name={record.from_name} phoneNumber={record.from_phone_number} />
        ),
      },
      {
        title: 'To',
        key: 'to',
        width: '14%',
        render: (_value: unknown, record: RingCentralCommunicationType) => (
          <ContactValue name={record.to_name} phoneNumber={record.to_phone_number} />
        ),
      },
      {
        title: (
          <RawStatusHeader>
            Raw Status / Date
            <Popover content="Status supplied by RingCentral. It can differ from Further's normalized outcome.">
              <RawStatusInfoButton type="button" aria-label="About Raw Status / Date">
                <InfoCircleOutlined />
              </RawStatusInfoButton>
            </Popover>
          </RawStatusHeader>
        ),
        key: 'status_date',
        width: '18%',
        render: (_value: unknown, record: RingCentralCommunicationType) => (
          <ValueContainer>
            <PrimaryValue>{getRawStatus(record)}</PrimaryValue>
            <SecondaryValue>{formatDate(record.occurred_at) || 'Unknown'}</SecondaryValue>
          </ValueContainer>
        ),
      },
      {
        title: 'Shopper',
        key: 'shopper',
        width: '12%',
        render: (_value: unknown, record: RingCentralCommunicationType) => (
          <ValueContainer>
            {record.matched_shop_request_label && <SecondaryValue>{record.matched_shop_request_label}</SecondaryValue>}
            <RingCentralRecordTag value={record.matching_status} />
          </ValueContainer>
        ),
      },
      {
        title: 'Media',
        key: 'media',
        width: '24%',
        render: (_value: unknown, record: RingCentralCommunicationType) => (
          <ValueContainer>
            <BadgeGroup>
              {record.recording_count > 0 && <Badge>{record.recording_count} recordings</Badge>}
              {record.transcript_count > 0 && <Badge>{record.transcript_count} transcripts</Badge>}
              {formatDuration(record.duration_seconds) && <Badge>{formatDuration(record.duration_seconds)}</Badge>}
            </BadgeGroup>
          </ValueContainer>
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: '6%',
        render: (_value: unknown, record: RingCentralCommunicationType) => (
          <ActionsCell className="actions-hover-reveal" onClick={(event) => event.stopPropagation()}>
            <Dropdown
              placement="bottomRight"
              menu={{
                items: [
                  {
                    label: 'Delete',
                    key: 'delete',
                    onClick: () => setRecordToDelete(record),
                  },
                ],
              }}
            >
              <SimpleListActionsButton>
                <SimpleListActionButtonContent>
                  <span>Actions</span>
                  <DownCaretIcon />
                </SimpleListActionButtonContent>
              </SimpleListActionsButton>
            </Dropdown>
          </ActionsCell>
        ),
      },
    ],
    []
  );

  return (
    <Container>
      <RingCentralRecordsStyledTable
        columns={columns}
        dataSource={items}
        rowKey={(record: RingCentralCommunicationType) => record.id}
        loading={loading}
        onRow={(record: RingCentralCommunicationType) => {
          return {
            onClick: () => {
              setSelectedRecordId(record.id);
            },
          };
        }}
        pagination={{
          size: 'small',
          position: ['bottomLeft'],
          showSizeChanger: false,
          current: filters.page,
          pageSize: filters.pageSize,
          total: totalItemsCount,
          onChange: (pageNumber: number) => {
            setPage(pageNumber);
          },
          showTotal: renderRecordsTotal,
        }}
      />
      <RingCentralRecordMediaModal recordId={selectedRecordId} onClose={() => setSelectedRecordId(null)} />
      <ConfirmModal
        open={Boolean(recordToDelete)}
        title="Delete Record?"
        description={`Are you sure you want to delete "${recordToDelete?.external_id}"? The record, its activity, and any media/recordings will be permanently removed.`}
        confirmLabel="Delete"
        onConfirm={() => deleteRecord()}
        onCancel={() => setRecordToDelete(null)}
        loading={isDeleting}
      />
    </Container>
  );
};

export default RingCentralRecordsTable;
